import type { ReactNode } from 'react'
import { ResponsiveContainer } from 'recharts'
import { cn } from '@/lib/cn'

/**
 * A sized box for a recharts chart. ResponsiveContainer measures its parent,
 * so the parent must have a real height, and `min-w-0` lets the box shrink
 * inside a grid cell instead of widening the page at 375px.
 */
export function ChartBox({
  height = 224,
  className,
  children,
}: {
  height?: number
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('min-w-0 w-full', className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as never}
      </ResponsiveContainer>
    </div>
  )
}

/** Swatch list under a chart, the dashboard's donut legend extracted. */
export function ChartLegend({ items, className }: { items: { label: ReactNode; fill: string; stroke?: string }[]; className?: string }) {
  return (
    <div className={cn('mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-foreground/70', className)}>
      {items.map((item, i) => (
        <p key={i} className="flex items-center gap-2">
          <span className="size-2.5 shrink-0 rounded-full border" style={{ background: item.fill, borderColor: item.stroke ?? item.fill }} />
          {item.label}
        </p>
      ))}
    </div>
  )
}

export function ChartEmpty({ height = 224, children = 'No data in this range.' }: { height?: number; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-center text-sm text-foreground/60" style={{ height }}>
      {children}
    </div>
  )
}
